/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2021-08-04 12:30:39
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import { stringifyRGBASpecialFormat } from 'src/helpers/color-helper';

import type {
  DefaultThemeSettingsDisplayType,
  DefaultThemeSettingsType
} from 'src/models/default-theme-settings';

/**
 * Build initial data for default theme settings
 *
 * @param  {DefaultThemeSettingsType} defaultLayoutSettings data from API
 *
 * @return {DefaultThemeSettingsDisplayType} initialValue
 */
export const buildInitialDefaultThemeSettingsValues = (
  defaultLayoutSettings: DefaultThemeSettingsType
) => {
  let layoutType = _.get(defaultLayoutSettings, 'layout_type', 'box');
  let viewAsType = _.get(defaultLayoutSettings, 'view_as_type', 'view_as_type_grid_list');

  // Fonts
  const productTitleFont = initFonts(defaultLayoutSettings, 'product_title_font');
  const productVendorFont = initFonts(defaultLayoutSettings, 'product_vendor_font');
  const productPriceFont = initFonts(defaultLayoutSettings, 'product_price_font');
  const productDesFont = initFonts(defaultLayoutSettings, 'product_des_font');

  let swatchByColorApply = _.get(defaultLayoutSettings, 'swatch_by_color_apply', '');
  swatchByColorApply = initSwatchApply(swatchByColorApply);
  let swatchByImgApply = _.get(defaultLayoutSettings, 'swatch_by_img_apply', '');
  swatchByImgApply = initSwatchApply(swatchByImgApply);
  let swatchByProImgApply = _.get(defaultLayoutSettings, 'swatch_by_pro_img_apply', '');
  swatchByProImgApply = initSwatchApply(swatchByProImgApply);
  let swatchByTextApply = _.get(defaultLayoutSettings, 'swatch_by_text_apply', '');
  swatchByTextApply = initSwatchApply(swatchByTextApply);

  // Map settings to form
  const general = {
    layoutType: [layoutType],
    showCollectionDes: _.get(defaultLayoutSettings, 'show_collection_des', true),
    showCollectionImage: _.get(defaultLayoutSettings, 'show_collection_image', true),
    collectionHeaderBg: _.get(defaultLayoutSettings, 'collection_header_bg', '#f6f6f8'),
    collectionHeaderText: _.get(defaultLayoutSettings, 'collection_header_text', '#000000'),
    viewAsType: [viewAsType],
    enableFilterStickyD: _.get(defaultLayoutSettings, 'enable_filter_sticky_d', false),
    enableFilterStickyM: _.get(defaultLayoutSettings, 'enable_filter_sticky_m', true),
    showViewAs: _.get(defaultLayoutSettings, 'show_view_as', true),
    showViewAsM: _.get(defaultLayoutSettings, 'show_view_as_m', true),
    showProductCount: _.get(defaultLayoutSettings, 'show_product_count', true),
    showProductCountM: _.get(defaultLayoutSettings, 'show_product_count_m', true),
    showSortBy: _.get(defaultLayoutSettings, 'show_sort_by', true),
    showSortByM: _.get(defaultLayoutSettings, 'show_sort_by_m', true)
  };
  const products = {
    // Product listing
    productItemType: _.get(defaultLayoutSettings, 'product_item_type', 'grid'),
    productItemLayout: _.get(defaultLayoutSettings, 'product_item_layout', 'no-border'),
    textAlignment: _.get(defaultLayoutSettings, 'text_alignment', 'left'),
    showVendor: _.get(defaultLayoutSettings, 'show_vendor', true),
    showPrice: _.get(defaultLayoutSettings, 'show_price', true),
    showProductReview: _.get(defaultLayoutSettings, 'show_product_review', false),
    showDetailWhenHover: _.get(defaultLayoutSettings, 'show_detail_when_hover', false),
    activeImageSwap: _.get(defaultLayoutSettings, 'active_image_swap', true),
    aspectRatio: _.get(defaultLayoutSettings, 'aspect_ratio', 'auto'),
    aspectRatioOther: _.get(defaultLayoutSettings, 'aspect_ratio_other', ''),
    productImgCrop: _.get(defaultLayoutSettings, 'product_img_crop', 'none'),
    productsPerPage: _.get(defaultLayoutSettings, 'products_per_page', 24),
    productsPerRow: _.get(defaultLayoutSettings, 'products_per_row', 3),
    productsPerRowM: _.get(defaultLayoutSettings, 'products_per_row_m', 2),

    // Product details
    productTitleColor: _.get(defaultLayoutSettings, 'product_title_color', '#3d4246'),
    productTitleFont: productTitleFont.fontFamily,
    productTitleFontStyle: productTitleFont.fontStyle,
    productTitleSize: _.get(defaultLayoutSettings, 'product_title_size', 14),
    productTitleTransform: _.get(defaultLayoutSettings, 'product_title_transform', 'capitalize'),
    productVendorColor: _.get(defaultLayoutSettings, 'product_vendor_color', '#969595'),
    productVendorFont: productVendorFont.fontFamily,
    productVendorFontStyle: productVendorFont.fontStyle,
    productVendorSize: _.get(defaultLayoutSettings, 'product_vendor_size', 12),
    productVendorTransform: _.get(defaultLayoutSettings, 'product_vendor_transform', 'uppercase'),
    productPriceColor: _.get(defaultLayoutSettings, 'product_price_color', '#222222'),
    productOldPriceColor: _.get(defaultLayoutSettings, 'product_old_price_color', '#969595'),
    productSalePriceColor: _.get(defaultLayoutSettings, 'product_sale_price_color', '#f30000'),
    productPriceFont: productPriceFont.fontFamily,
    productPriceFontStyle: productPriceFont.fontStyle,
    productPriceSize: _.get(defaultLayoutSettings, 'product_price_size', 14),
    productDesColor: _.get(defaultLayoutSettings, 'product_des_color', '#3a3a3a'),
    productDesFont: productDesFont.fontFamily,
    productDesFontStyle: productDesFont.fontStyle,
    productDesSize: _.get(defaultLayoutSettings, 'product_des_size', 14),

    // Product swatch
    swatchEnable: _.get(defaultLayoutSettings, 'swatch_enable', true),
    showSwatchTooltip: _.get(defaultLayoutSettings, 'show_swatch_tooltip', true),
    swatchChangeImg: [_.get(defaultLayoutSettings, 'swatch_change_img', 'hover')],
    swatchByColorApply: swatchByColorApply,
    swatchByColorShape: [_.get(defaultLayoutSettings, 'swatch_by_color_shape', 'circle')],
    swatchByImgApply: swatchByImgApply,
    swatchByImgShape: [_.get(defaultLayoutSettings, 'swatch_by_img_shape', 'circle')],
    swatchByProImgApply: swatchByProImgApply,
    swatchByProImgShape: [_.get(defaultLayoutSettings, 'swatch_by_pro_img_shape', 'circle')],
    swatchByTextApply: swatchByTextApply,
    swatchByTextShape: [_.get(defaultLayoutSettings, 'swatch_by_text_shape', 'square')],
    swatchByTextBg: _.get(defaultLayoutSettings, 'swatch_by_text_bg', '#ffffff'),
    swatchByTextColor: _.get(defaultLayoutSettings, 'swatch_by_text_color', '#3d4246'),
    swatchByTextBorder: _.get(defaultLayoutSettings, 'swatch_by_text_border', '#ebebeb'),

    // Product label
    labelPosition: _.get(defaultLayoutSettings, 'label_position', 'top_left'),
    labelType: _.get(defaultLayoutSettings, 'label_type', 'rectangle'),
    saleLabelEnable: _.get(defaultLayoutSettings, 'sale_label_enable', true),
    saleLabelDisplay: [_.get(defaultLayoutSettings, 'sale_label_display', 'text')],
    saleLabelBg: _.get(defaultLayoutSettings, 'sale_label_bg', '#e02d00'),
    saleLabelText: _.get(defaultLayoutSettings, 'sale_label_text', '#ffffff'),
    saleLabelImg: _.get(defaultLayoutSettings, 'sale_label_img', ''),
    saleLabelImgSize: _.get(defaultLayoutSettings, 'sale_label_img_size', 0),
    soldOutEnable: _.get(defaultLayoutSettings, 'sold_out_enable', true),
    soldOutDisplay: [_.get(defaultLayoutSettings, 'sold_out_display', 'text')],
    soldOutBg: _.get(defaultLayoutSettings, 'sold_out_bg', '#d2d8db'),
    soldOutText: _.get(defaultLayoutSettings, 'sold_out_text', '#ffffff'),
    soldOutImg: _.get(defaultLayoutSettings, 'sold_out_img', ''),
    soldOutImgSize: _.get(defaultLayoutSettings, 'sold_out_img_size', 0),
    tagLabelEnable: _.get(defaultLayoutSettings, 'tag_label_enable', false),
    tagLabelBg: _.get(defaultLayoutSettings, 'tag_label_bg', '#4caf50'),
    tagLabelText: _.get(defaultLayoutSettings, 'tag_label_text', '')
  };

  return { general, products };
};

/**
 * Build data for saving default layout settings
 *
 * @param  {Object} data form data
 * @param  {Object} defaultLayoutSettings default theme settings from API
 *
 * @return {Object} value value for sending API
 */
export const buildDefaultThemeSettingsValues = (data: DefaultThemeSettingsDisplayType) => {
  let { general = {}, products = {} } = data;

  let layoutType = _.get(general, 'layoutType[0]', 'box');
  let viewAsType = _.get(general, 'viewAsType[0]', 'view_as_type_grid_list');

  // Get image name if upload file, else get existing value
  let saleLabelImg = _.get(products, 'saleLabelImg', '');
  saleLabelImg = _.isObject(saleLabelImg) ? saleLabelImg.name : saleLabelImg;
  let soldOutImg = _.get(products, 'soldOutImg', '');
  soldOutImg = _.isObject(soldOutImg) ? soldOutImg.name : soldOutImg;

  let swatchChangeImg = _.get(products, 'swatchChangeImg[0]', 'hover');

  let swatchByColorApply = _.get(products, 'swatchByColorApply', []);
  swatchByColorApply = buildSwatchApply(swatchByColorApply);
  let swatchByColorShape = _.get(products, 'swatchByColorShape[0]', 'circle');

  let swatchByImgApply = _.get(products, 'swatchByImgApply', []);
  swatchByImgApply = buildSwatchApply(swatchByImgApply);
  let swatchByImgShape = _.get(products, 'swatchByImgShape[0]', 'circle');

  let swatchByProImgApply = _.get(products, 'swatchByProImgApply', []);
  swatchByProImgApply = buildSwatchApply(swatchByProImgApply);
  let swatchByProImgShape = _.get(products, 'swatchByProImgShape[0]', 'circle');

  let swatchByTextApply = _.get(products, 'swatchByTextApply', []);
  swatchByTextApply = buildSwatchApply(swatchByTextApply);
  let swatchByTextShape = _.get(products, 'swatchByTextShape[0]', 'square');

  const convertSettings = {};

  // General settings
  convertSettings.layout_type = layoutType;
  convertSettings.show_collection_des = _.get(general, 'showCollectionDes', true);
  convertSettings.show_collection_image = _.get(general, 'showCollectionImage', true);
  convertSettings.collection_header_bg = _.get(general, 'collectionHeaderBg', '#f6f6f8');
  convertSettings.collection_header_text = _.get(general, 'collectionHeaderText', '#000000');
  convertSettings.view_as_type = viewAsType;
  convertSettings.enable_filter_sticky_d = _.get(general, 'enableFilterStickyD', false);
  convertSettings.enable_filter_sticky_m = _.get(general, 'enableFilterStickyM', true);
  convertSettings.show_view_as = _.get(general, 'showViewAs', true);
  convertSettings.show_view_as_m = _.get(general, 'showViewAsM', true);
  convertSettings.show_product_count = _.get(general, 'showProductCount', true);
  convertSettings.show_product_count_m = _.get(general, 'showProductCountM', true);
  convertSettings.show_sort_by = _.get(general, 'showSortBy', true);
  convertSettings.show_sort_by_m = _.get(general, 'showSortByM', true);

  // Convert product item settings
  // - Product listing
  convertSettings.product_item_type = _.get(products, 'productItemType', 'grid');
  convertSettings.product_item_layout = _.get(products, 'productItemLayout', 'no-border');
  convertSettings.text_alignment = _.get(products, 'textAlignment', 'left');
  convertSettings.show_vendor = _.get(products, 'showVendor', true);
  convertSettings.show_price = _.get(products, 'showPrice', true);
  convertSettings.show_product_review = _.get(products, 'showProductReview', false);
  convertSettings.show_detail_when_hover = _.get(products, 'showDetailWhenHover', false);
  convertSettings.active_image_swap = _.get(products, 'activeImageSwap', true);
  convertSettings.aspect_ratio = _.get(products, 'aspectRatio', 'auto');
  convertSettings.aspect_ratio_other = _.get(products, 'aspectRatioOther', false);
  convertSettings.product_img_crop = _.get(products, 'productImgCrop', 'none');
  convertSettings.products_per_page = _.get(products, 'productsPerPage', 24);
  convertSettings.products_per_row = _.get(products, 'productsPerRow', 3);
  convertSettings.products_per_row_m = _.get(products, 'productsPerRowM', 2);

  // - Product details
  convertSettings.product_title_color = _.get(products, 'productTitleColor', '#3d4246');
  convertSettings.product_title_font = buildFonts(
    products,
    'productTitleFont',
    'productTitleFontStyle'
  );
  convertSettings.product_title_size = _.get(products, 'productTitleSize', 14);
  convertSettings.product_title_transform = _.get(products, 'productTitleTransform', 'capitalize');
  convertSettings.product_vendor_color = _.get(products, 'productVendorColor', '#969595');
  convertSettings.product_vendor_font = buildFonts(
    products,
    'productVendorFont',
    'productVendorFontStyle'
  );
  convertSettings.product_vendor_size = _.get(products, 'productVendorSize', 12);
  convertSettings.product_vendor_transform = _.get(products, 'productVendorTransform', 'uppercase');
  convertSettings.product_price_color = _.get(products, 'productPriceColor', '#222222');
  convertSettings.product_old_price_color = _.get(products, 'productOldPriceColor', '#969595');
  convertSettings.product_sale_price_color = _.get(products, 'productSalePriceColor', '#f30000');
  convertSettings.product_price_font = buildFonts(
    products,
    'productPriceFont',
    'productPriceFontStyle'
  );
  convertSettings.product_price_size = _.get(products, 'productPriceSize', 14);
  convertSettings.product_des_color = _.get(products, 'productDesColor', '#3a3a3a');
  convertSettings.product_des_font = buildFonts(products, 'productDesFont', 'productDesFontStyle');
  convertSettings.product_des_size = _.get(products, 'productDesSize', 14);

  // - Product swatch
  convertSettings.swatch_enable = _.get(products, 'swatchEnable', true);
  convertSettings.show_swatch_tooltip = _.get(products, 'showSwatchTooltip', true);
  convertSettings.swatch_change_img = swatchChangeImg;
  convertSettings.swatch_by_color_apply = swatchByColorApply;
  convertSettings.swatch_by_color_shape = swatchByColorShape;
  convertSettings.swatch_by_img_apply = swatchByImgApply;
  convertSettings.swatch_by_img_shape = swatchByImgShape;
  convertSettings.swatch_by_pro_img_apply = swatchByProImgApply;
  convertSettings.swatch_by_pro_img_shape = swatchByProImgShape;
  convertSettings.swatch_by_text_apply = swatchByTextApply;
  convertSettings.swatch_by_text_shape = swatchByTextShape;
  convertSettings.swatch_by_text_bg = _.get(products, 'swatchByTextBg', '#ffffff');
  convertSettings.swatch_by_text_color = _.get(products, 'swatchByTextColor', '#3d4246');
  convertSettings.swatch_by_text_border = _.get(products, 'swatchByTextBorder', '#ebebeb');

  // - Product label
  convertSettings.label_position = _.get(products, 'labelPosition', 'top_left');
  convertSettings.label_type = _.get(products, 'labelType', 'rectangle');
  convertSettings.sale_label_enable = _.get(products, 'saleLabelEnable', true);
  const saleLabelDisplay = _.get(products, 'saleLabelDisplay', 'text');
  convertSettings.sale_label_display = _.isArray(saleLabelDisplay)
    ? saleLabelDisplay[0]
    : saleLabelDisplay;
  convertSettings.sale_label_bg = _.get(products, 'saleLabelBg', '#e02d00');
  convertSettings.sale_label_text = _.get(products, 'saleLabelText', '#ffffff');
  convertSettings.sale_label_img = saleLabelImg;
  convertSettings.sold_out_enable = _.get(products, 'soldOutEnable', true);
  const soldOutDisplay = _.get(products, 'soldOutDisplay', 'text');
  convertSettings.sold_out_display = _.isArray(soldOutDisplay) ? soldOutDisplay[0] : soldOutDisplay;
  convertSettings.sold_out_bg = _.get(products, 'soldOutBg', '#d2d8db');
  convertSettings.sold_out_text = _.get(products, 'soldOutText', '#ffffff');
  convertSettings.sold_out_img = soldOutImg;
  convertSettings.tag_label_enable = _.get(products, 'tagLabelEnable', false);
  convertSettings.tag_label_bg = _.get(products, 'tagLabelBg', '#4caf50');
  convertSettings.tag_label_text = _.get(products, 'tagLabelText', '#ffffff');

  return convertSettings;
};

/**
 * Convert HSV color to RGBA string under format rbga(r||g||b||a)
 * @param  {object} hsvColor
 * @return {string}          rgba string color
 */
export const convertColorToSpecialFormat = (color: Object) => {
  // Return original color if it is not hsv
  if (!_.has(color, 'rgb')) {
    return color;
  }

  return stringifyRGBASpecialFormat(color.rgb);
};

/**
 * Init swatch apply, convert data from [{value}] to [{value, label}]
 * @param  {array} swatchValues
 * @return {string}          rgba string color
 */
const initSwatchApply = swatchValues => {
  if (!swatchValues) return [];

  const values = swatchValues ? swatchValues.split(',') : [];
  if (_.isEmpty(values) || (values.length === 1 && values[0] === '')) return [];

  return values.map(swatch => {
    return { value: swatch, label: swatch };
  });
};

/**
 * Build swatch apply, convert data from [{value, label}] to [value]
 * @param  {array} swatchValues
 * @return {string}          rgba string color
 */
const buildSwatchApply = swatchValues => {
  if (!swatchValues || !_.isArray(swatchValues)) return [];
  const map = swatchValues.map(swatch => swatch.value);
  return map.join(',');
};

/**
 * Init fonts
 * @param {*} settings
 * @param {*} fontField
 * @returns
 */
const initFonts = (settings, fontField) => {
  const font = _.get(settings, fontField, {});
  if (_.isEmpty(font)) return { fontFamily: { label: '', value: '' }, fontStyle: '' };

  const { family, weight = '', style = '' } = font;
  const variant = `${weight}${style}`;
  return {
    fontFamily: { label: family, value: family },
    // fontStyle: { label: variant, value: variant }
    fontStyle: variant
  };
};
/**
 * Build font
 * @param {*} products
 * @param {*} fontField
 * @param {*} fontStyleField
 * @returns
 */
const buildFonts = (products, fontField, fontStyleField) => {
  const fontFamily = _.get(products, `${fontField}.value`, {});
  const fontStyle = _.get(products, fontStyleField, '');

  if (_.isEmpty(fontFamily)) return '';

  const font = { family: fontFamily };

  // Parse font style and font weight, ex: 500italic
  const regex = /^\d+/g;
  const matches = fontStyle.match(regex);
  if (_.isArray(matches)) {
    font.weight = matches[0];
    const style = fontStyle.replace(matches[0], '');
    if (style) {
      font.style = style;
    }
  } else {
    font.style = fontStyle;
  }

  return font;
};
